import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, DEFAULT_OG_ALT, isPrivatePath, canonicalPath } from '@/config/seo';

export interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  url?: string;
  type?: 'website' | 'article' | 'video.other';
  noIndex?: boolean;
  canonicalUrl?: string;
  structuredData?: object;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  imageWidth?: number;
  imageHeight?: number;
}

export function SEOHead({ title, description, image = DEFAULT_OG_IMAGE, imageAlt = DEFAULT_OG_ALT, url, type = 'website', noIndex = false, canonicalUrl, structuredData, publishedTime, modifiedTime, author, imageWidth, imageHeight }: SEOHeadProps) {
  const location = useLocation();
  const base = (import.meta.env.VITE_SITE_URL || SITE_URL).replace(/\/$/, '');
  // Canonical identity never contains session invites, auth tokens or tracking params.
  const source = new URL(canonicalUrl || url || canonicalPath(location.pathname), base);
  const canonical = new URL(canonicalPath(source.pathname), base);
  const page = new URLSearchParams(location.search).get('page');
  if (!canonicalUrl && page && /^\d+$/.test(page) && Number(page) > 1 && ['/replays', '/clips', '/news'].includes(location.pathname)) canonical.searchParams.set('page', page);
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const hidden = noIndex || isPrivatePath(location.pathname) || location.pathname.startsWith('/viewer/');
  const imageUrl = new URL(image, base).href;
  const robots = hidden ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';
  return <Helmet>
    <title>{fullTitle}</title>
    <meta name="description" content={description} />
    <meta name="robots" content={robots} />
    <link rel="canonical" href={canonical.href} />
    <meta property="og:type" content={type} />
    <meta property="og:url" content={canonical.href} />
    <meta property="og:title" content={fullTitle} />
    <meta property="og:description" content={description} />
    <meta property="og:image" content={imageUrl} />
    <meta property="og:image:alt" content={imageAlt} />
    {(imageWidth || image === DEFAULT_OG_IMAGE) && <meta property="og:image:width" content={String(imageWidth || 1200)} />}
    {(imageHeight || image === DEFAULT_OG_IMAGE) && <meta property="og:image:height" content={String(imageHeight || 630)} />}
    {publishedTime && <meta property="article:published_time" content={publishedTime} />}
    {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
    {author && <meta name="author" content={author} />}
    <meta property="og:site_name" content={SITE_NAME} />
    <meta property="og:locale" content="en_US" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={fullTitle} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={imageUrl} />
    <meta name="twitter:image:alt" content={imageAlt} />
    {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData).replace(/</g, '\\u003c')}</script>}
  </Helmet>;
}
export default SEOHead;
